import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { EventSource } from 'eventsource';
import { useCurrentOrgContext } from 'src/contexts/OrgContext';
import { FAST_API_URL, ROS_URL } from 'src/config';

interface UseSSEOptions<T> {
  enabled?: boolean;
  onMessage?: (data: T) => void;
  onError?: (error: Error) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  reconnectInterval?: number;
  eventName?: string;
  params?: Record<string, string>;
  useRos?: boolean;
}

export function useSSE<T>(url: string, options: UseSSEOptions<T> = {}) {
  const {
    enabled = true,
    onMessage,
    onError,
    onConnect,
    onDisconnect,
    reconnectInterval = 3000,
    eventName,
    params = {},
    useRos = false,
  } = options;

  const { orgId } = useCurrentOrgContext();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(false);
  const isFirstRender = useRef(true);
  const isConnecting = useRef(false);
  const reconnectPending = useRef(false);

  // Keep callbacks in refs so connect() always uses latest without re-creating
  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);

  useEffect(() => {
    onMessageRef.current = onMessage;
    onErrorRef.current = onError;
    onConnectRef.current = onConnect;
    onDisconnectRef.current = onDisconnect;
  }, [onMessage, onError, onConnect, onDisconnect]);

  const stableParams = useMemo(
    () => params,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(params)]
  );

  const connect = useCallback(async () => {
    if (!enabled || !isAuthenticated || !mountedRef.current) {
      if (!enabled) {
        eventSourceRef.current?.close();
        eventSourceRef.current = null;
        setIsConnected(false);
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      }
      return;
    }
    if (isConnecting.current) {
      // A connect is already awaiting its token. Dropping this one would
      // strand the stream on whichever org the in-flight call captured, so
      // record it and run it once that call settles.
      reconnectPending.current = true;
      return;
    }
    isConnecting.current = true;

    try {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      const token = await getAccessTokenSilently();
      const queryParams = new URLSearchParams(stableParams).toString();
      let fullUrl = queryParams ? `${url}?${queryParams}` : url;

      if (fullUrl.startsWith('/')) {
        fullUrl = fullUrl.slice(1);
      }

      if (useRos) {
        fullUrl = `${ROS_URL}/${fullUrl}`;
      } else {
        fullUrl = `${FAST_API_URL}/${fullUrl}`;
      }

      eventSourceRef.current = new EventSource(fullUrl, {
        fetch: (input, init) =>
          fetch(input, {
            ...init,
            headers: {
              ...init.headers,
              'x-org-id': orgId || '',
              Authorization: `Bearer ${token}`,
            },
          }),
      });

      eventSourceRef.current.onopen = () => {
        if (!mountedRef.current) return;
        setIsConnected(true);
        setError(null);
        onConnectRef.current?.();
      };

      eventSourceRef.current.addEventListener(eventName ?? 'update', (event: MessageEvent) => {
        if (!mountedRef.current) return;
        try {
          const messageData = JSON.parse(event.data) as T;
          setData(messageData);
          onMessageRef.current?.(messageData);
        } catch (err) {
          console.error('Failed to parse SSE message:', err);
        }
      });

      eventSourceRef.current.onerror = (err) => {
        if (!mountedRef.current) return;
        console.error('SSE connection error:', err);
        const error = new Error('SSE connection error');
        setError(error);
        setIsConnected(false);
        onErrorRef.current?.(error);
        onDisconnectRef.current?.();
        eventSourceRef.current?.close();

        if (enabled && reconnectInterval > 0) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current) connectRef.current();
          }, reconnectInterval);
        }
      };
    } catch (err) {
      if (mountedRef.current) {
        const error = err as Error;
        console.error('Failed to create SSE connection:', error);
        setError(error);
        onErrorRef.current?.(error);
      }
    } finally {
      isConnecting.current = false;
      if (reconnectPending.current && mountedRef.current) {
        reconnectPending.current = false;
        connectRef.current();
      }
    }
  }, [
    url,
    enabled,
    isAuthenticated,
    getAccessTokenSilently,
    reconnectInterval,
    stableParams,
    orgId,
    eventName,
    useRos,
  ]);

  // Stable ref to connect — used by onerror, online handler, and mount effect
  // so none of them need connect in their deps and won't trigger re-runs
  const connectRef = useRef(connect);
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  // Mount/unmount only — never re-runs due to connect identity changes
  useEffect(() => {
    mountedRef.current = true;
    connectRef.current();

    return () => {
      mountedRef.current = false;
      eventSourceRef.current?.close();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  // Reconnect when key params change, skipping the initial mount
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // The last message belongs to the stream being left, and an org switch
    // leaves this hook mounted, so nothing else drops it.
    setData(null);
    connectRef.current();
  }, [url, enabled, stableParams, orgId, eventName]);

  // Handle online/offline (PWA) — mount/unmount only, uses ref to avoid dep on connect
  useEffect(() => {
    const handleOnline = () => connectRef.current();
    const handleOffline = () => {
      eventSourceRef.current?.close();
      setIsConnected(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { data, error, isConnected };
}
